import { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { AccountBalanceOutline, InfoOutline } from '@eqi-investimentos/icons/web';
import { Flex, Text, Tooltip } from '@eqi-investimentos/react';
import { Icon } from '@/components/atoms/Icon';
import { Thumb } from '@/components/atoms/Thumb';
import { MonetaryVisibilityButton } from '@/components/molecules/MonetaryVisibilityButton';
import { useInvestViewModel } from '@/viewModels/InvestViewModel/InvestViewModel';
import { investStep } from '@/viewModels/InvestViewModel/utils';
import { formatNumberToPTBRCurrency } from '@/utils/format';
import { IfRender } from '@/utils/jsx';
import { AppsAndBrokersSection } from './components/AppsAndBrokersSection/AppsAndBrokersSection';
import { BrokersSection } from './components/BrokersSection';
import { ConfirmSendOrderAdvisor } from './components/ConfirmSendOrderAdvisor';
import { SectionMyConsultancy } from './components/SectionMyConsultancy';
import { SelectAssetSection } from './components/SelectAssetSection';
import { SendOrderAdvisorSection } from './components/SendOrderAdvisorSection/SendOrderAdvisorSection';
import { InvestViewSkeleton } from './Skeleton';
export const InvestView = () => {
  const {
    t
  } = useTranslation('common');
  const {
    advisor,
    investmentModeView,
    isEqiDigital,
    isLoading,
    walletBalances,
    order,
    setOrderToSend,
    sendOrder,
    setInvestStep
  } = useInvestViewModel();
  useEffect(() => {
    window.dispatchEvent(new Event('recheck-monetary-visibility'));
  }, [investmentModeView, isLoading, order, walletBalances?.balances]);
  return <Flex id={CONTAINER_WIDGET_ID} flexDir="column" gap="xl" pb="4xl" data-sentry-element="Flex" data-sentry-component="InvestView" data-sentry-source-file="InvestView.tsx">
      <IfRender condition={isLoading} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
        <InvestViewSkeleton data-sentry-element="InvestViewSkeleton" data-sentry-source-file="InvestView.tsx" />
      </IfRender>
      <IfRender condition={!isLoading} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
        <IfRender condition={investmentModeView === investStep[0]} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
          <Flex flexDir="column" gap="2xl" px="2xl" py="l" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
            <Flex justifyContent="space-between" alignItems="center" w="full" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
              <Flex flexDir="column" gap="2xs" w="full" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
                <Flex gap="3xs" w="full" align="center" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
                  <Text variant="l" color="content.primary" fontWeight="light" data-sentry-element="Text" data-sentry-source-file="InvestView.tsx">
                    {t('screens.invest.amountTitle')}
                  </Text>
                  <Tooltip label={t('screens.invest.tooltipBalance')} fontSize="xs" px="xs" color="white" bgColor="#1f2937" borderRadius="base" data-sentry-element="Tooltip" data-sentry-source-file="InvestView.tsx">
                    <Flex justifyContent="center" h="5" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
                      <InfoOutline w={16} h={16} color="content.brand" cursor="pointer" data-sentry-element="InfoOutline" data-sentry-source-file="InvestView.tsx" />
                    </Flex>
                  </Tooltip>
                </Flex>
                <Text className="hide-monetary-left" size="h5" color="content.primary" data-sentry-element="Text" data-sentry-source-file="InvestView.tsx">
                  {formatNumberToPTBRCurrency(walletBalances?.total)}
                </Text>
              </Flex>
              <MonetaryVisibilityButton widgetId={CONTAINER_WIDGET_ID} display={{
              base: 'none',
              md: 'flex'
            }} data-sentry-element="MonetaryVisibilityButton" data-sentry-source-file="InvestView.tsx" />
            </Flex>

            <Flex gap="m" w="full" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
              {walletBalances?.balances.map(balance => <Flex key={balance.label} flexDir="column" gap="2" minW="5.5rem">
                  <Flex w="full" align="center" gap="s">
                    <IfRender condition={balance?.label.toLowerCase() === 'eqi'}>
                      <Thumb src="/assets/eqi-logo.svg" width={20} height={20} fit="cover" alt="Logotipo quadrado com o fundo laranja e as letras EQI em preto" />
                    </IfRender>
                    <IfRender condition={balance?.label.toLowerCase() !== 'eqi'}>
                      <AccountBalanceOutline width={20} height={20} color="content.brand" />
                    </IfRender>
                    <Text size="l" color="content.primary">
                      {balance?.label}
                    </Text>
                  </Flex>
                  <Text size="l" color="content.secondary">
                    {formatNumberToPTBRCurrency(balance?.value)}
                  </Text>
                </Flex>)}
            </Flex>
            <IfRender condition={walletBalances?.total === 0} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
              <Flex w="full" bgColor="background.brandLight" borderRadius="lg" px="m" py="s" gap="l" alignItems="center" justifyContent="center" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
                <Icon iconName="AlertCircleFilled" width={24} height={24} color="content.primary" data-sentry-element="Icon" data-sentry-source-file="InvestView.tsx" />
                <Flex flexDir="column" h="full" gap="3xs" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
                  <Text fontWeight="medium" color="content.primary" size="xl" data-sentry-element="Text" data-sentry-source-file="InvestView.tsx">
                    {t('screens.invest.noBalance.title')}
                  </Text>
                  <Text size="l" color="content.primary" data-sentry-element="Text" data-sentry-source-file="InvestView.tsx">
                    {t('screens.invest.noBalance.description')}
                  </Text>
                </Flex>
              </Flex>
            </IfRender>
          </Flex>
          <IfRender condition={walletBalances?.total > 0} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
            <SendOrderAdvisorSection handleSelectAsset={() => {
            setInvestStep(investStep[1]);
          }} handleSendOrder={setOrderToSend} data-sentry-element="SendOrderAdvisorSection" data-sentry-source-file="InvestView.tsx" />
          </IfRender>
          <AppsAndBrokersSection balance={walletBalances?.total} data-sentry-element="AppsAndBrokersSection" data-sentry-source-file="InvestView.tsx" />
          {advisor && <SectionMyConsultancy advisorData={advisor} isEqiDigital={isEqiDigital} />}
        </IfRender>
        <Flex px="2xl" w="full" data-sentry-element="Flex" data-sentry-source-file="InvestView.tsx">
          <IfRender condition={investmentModeView === investStep[1]} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
            <SelectAssetSection data-sentry-element="SelectAssetSection" data-sentry-source-file="InvestView.tsx" />
          </IfRender>
          <IfRender condition={investmentModeView === investStep[2]} data-sentry-element="IfRender" data-sentry-source-file="InvestView.tsx">
            <BrokersSection data-sentry-element="BrokersSection" data-sentry-source-file="InvestView.tsx" />
          </IfRender>
        </Flex>

        {order && investmentModeView === investStep[3] && <ConfirmSendOrderAdvisor orderDataToSend={order} handleBack={() => setInvestStep(investStep[0])} handleConfirmAndSend={sendOrder} />}
      </IfRender>
    </Flex>;
};
const CONTAINER_WIDGET_ID = 'invest-drawer-widget';